import { useQuery, useMutation } from '@tanstack/react-query'
import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import { toast } from 'sonner'
import { useAuthStore } from '../../store/authStore'
import { organizationsApi } from '../../services/api'
import type {
  Organization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  CreateSubOrganizationRequest,
  SetDefaultOrganizationRequest,
  OrganizationMembership,
  AddMemberRequest,
  CreateUserAndAddToOrgRequest,
  UpdateMemberRoleRequest,
  OrganizationRole,
  CreateRoleRequest,
  UpdateRoleRequest,
  RolePermissionsResponse,
  GrantPermissionsRequest,
  RevokePermissionsRequest,
  ObjectPermissionsResponse,
  GrantObjectPermissionRequest,
  RevokeObjectPermissionRequest,
  SetOrganizationLimitRequest,
  SetOrganizationLimitResponse,
  OrganizationUsageResponse,
  SetUserLimitRequest,
  SetUserLimitResponse,
  UserLimitsListResponse,
  UserUsageResponse,
  ResourceType,
  OrganizationIpRange,
  BatchCreateIpRangeRequest,
  BatchCreateIpRangeResponse,
  BatchUpdateIpRangeRequest,
  BatchUpdateIpRangeResponse,
  ParentIpRangesResponse,
} from '../../app/modules/accounts/components/organization/types'

// ============================================
// ORGANIZATIONS QUERIES
// ============================================

/**
 * Hook for fetching all organizations the authenticated user has access to
 * Automatically syncs org list to Zustand store
 */
export function useOrganizations() {
  // Don't fetch organizations on auth pages (login, signup, forgot-password, etc.)
  const isAuthPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/auth')

  return useQuery({
    queryKey: queryKeys.organizations.list,
    queryFn: async (): Promise<Organization[]> => {
      const data = await organizationsApi.getAll()
      useAuthStore.getState().SetOrgList(data)
      return data
    },
    enabled: !isAuthPage, // Only fetch when not on auth pages
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    retry: (failureCount, error: any) => {
      // Don't retry on 401 (unauthorized)
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 1
    },
  })
}

/**
 * Hook for fetching a specific organization by ID
 */
export function useOrganization(organizationId: string | null) {
  return useQuery({
    queryKey: queryKeys.organizations.detail(organizationId || ''),
    queryFn: async (): Promise<Organization> => {
      return await organizationsApi.getById(organizationId!)
    },
    enabled: !!organizationId,
    retry: (failureCount, error: any) => {
      // Don't retry on 404
      if (error?.response?.status === 404) return false
      return failureCount < 1
    },
  })
}

/**
 * Hook for fetching the user's current default organization
 * Automatically syncs org ID to Zustand store
 */
export function useCurrentOrganization() {
  // Don't fetch current organization on auth pages (login, signup, forgot-password, etc.)
  const isAuthPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/auth')

  return useQuery({
    queryKey: queryKeys.organizations.current,
    queryFn: async (): Promise<Organization> => {
      const data = await organizationsApi.getCurrent()
      useAuthStore.getState().SetOrgId(data.id)
      return data
    },
    enabled: !isAuthPage, // Only fetch when not on auth pages
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 401 (unauthorized)
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 1
    },
  })
}

// ============================================
// ORGANIZATIONS MUTATIONS
// ============================================

/**
 * Hook for creating a new root organization
 */
export function useCreateOrganization() {
  return useMutation({
    mutationFn: async (data: CreateOrganizationRequest): Promise<Organization> => {
      return await organizationsApi.create(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for updating organization details
 */
export function useUpdateOrganization() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      data,
    }: {
      organizationId: string
      data: UpdateOrganizationRequest
    }): Promise<Organization> => {
      return await organizationsApi.update(organizationId, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all })
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.detail(variables.organizationId),
      })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for deleting an organization and all its descendants
 */
export function useDeleteOrganization() {
  return useMutation({
    mutationFn: async (organizationId: string): Promise<void> => {
      await organizationsApi.delete(organizationId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for creating a child organization under an existing organization
 */
export function useCreateSubOrganization() {
  return useMutation({
    mutationFn: async ({
      parentOrganizationId,
      data,
    }: {
      parentOrganizationId: string
      data: CreateSubOrganizationRequest
    }): Promise<Organization> => {
      return await organizationsApi.createSubOrganization(parentOrganizationId, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for setting the user's default/active organization
 */
export function useSetDefaultOrganization() {
  return useMutation({
    mutationFn: async (data: SetDefaultOrganizationRequest): Promise<OrganizationMembership> => {
      return await organizationsApi.setDefault(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.current })
    },
    onError: () => {

    },
  })
}

// ============================================
// IP RANGE QUERIES
// ============================================

/**
 * Hook for fetching all IP ranges for an organization
 */
export function useIpRanges(orgId: string | null) {
  return useQuery({
    queryKey: queryKeys.ipRanges.list(orgId || ''),
    queryFn: async (): Promise<OrganizationIpRange[]> => {
      return await organizationsApi.getIpRanges(orgId!)
    },
    enabled: !!orgId,
    staleTime: QUERY_CONFIG.LISTS.staleTime,
  })
}

/**
 * Hook for fetching a single IP range by ID
 */
export function useIpRange(orgId: string | null, ipRangeId: string | null) {
  return useQuery({
    queryKey: queryKeys.ipRanges.detail(orgId || '', ipRangeId || ''),
    queryFn: async (): Promise<OrganizationIpRange> => {
      return await organizationsApi.getIpRange(orgId!, ipRangeId!)
    },
    enabled: !!orgId && !!ipRangeId,
  })
}

/**
 * Hook for fetching parent organization's IP ranges
 */
export function useParentIpRanges(orgId: string | null) {
  return useQuery({
    queryKey: queryKeys.ipRanges.parentRanges(orgId || ''),
    queryFn: async (): Promise<ParentIpRangesResponse> => {
      return await organizationsApi.getParentIpRanges(orgId!)
    },
    enabled: !!orgId,
    staleTime: QUERY_CONFIG.LISTS.staleTime,
  })
}

// ============================================
// IP RANGE MUTATIONS
// ============================================

/**
 * Hook for batch creating IP ranges
 */
export function useBatchCreateIpRanges() {
  return useMutation({
    mutationFn: async ({
      orgId,
      data,
    }: {
      orgId: string
      data: BatchCreateIpRangeRequest
    }): Promise<BatchCreateIpRangeResponse> => {
      return await organizationsApi.batchCreateIpRanges(orgId, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ipRanges.list(variables.orgId) })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for batch updating IP ranges (replaces all IP ranges atomically)
 */
export function useBatchUpdateIpRanges() {
  return useMutation({
    mutationFn: async ({
      orgId,
      data,
    }: {
      orgId: string
      data: BatchUpdateIpRangeRequest
    }): Promise<BatchUpdateIpRangeResponse> => {
      return await organizationsApi.batchUpdateIpRanges(orgId, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ipRanges.list(variables.orgId) })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for deleting a single IP range
 */
export function useDeleteIpRange() {
  return useMutation({
    mutationFn: async ({ orgId, ipRangeId }: { orgId: string; ipRangeId: string }): Promise<void> => {
      await organizationsApi.deleteIpRange(orgId, ipRangeId)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ipRanges.list(variables.orgId) })
    },
    onError: () => {

    },
  })
}

// ============================================
// MEMBERSHIP QUERIES
// ============================================

/**
 * Hook for fetching all members of an organization
 * @param includeDescendants - If true, includes members from all descendant organizations
 */
export function useOrganizationMembers(
  organizationId: string | null,
  includeDescendants?: boolean
) {
  return useQuery({
    queryKey: queryKeys.memberships.list(organizationId || '', includeDescendants),
    queryFn: async (): Promise<OrganizationMembership[]> => {
      return await organizationsApi.getMembers(organizationId!, includeDescendants)
    },
    enabled: !!organizationId,
    staleTime: 3 * 60 * 1000, // 3 minutes
  })
}

/**
 * Hook for fetching details of a specific membership
 */
export function useMembership(organizationId: string | null, membershipId: string | null) {
  return useQuery({
    queryKey: queryKeys.memberships.detail(organizationId || '', membershipId || ''),
    queryFn: async (): Promise<OrganizationMembership> => {
      return await organizationsApi.getMember(organizationId!, membershipId!)
    },
    enabled: !!organizationId && !!membershipId,
  })
}

// ============================================
// MEMBERSHIP MUTATIONS
// ============================================

/**
 * Hook for adding a user to an organization with a specific role
 */
export function useAddMember() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      data,
    }: {
      organizationId: string
      data: AddMemberRequest
    }) => {
      return await organizationsApi.addMember(organizationId, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.memberships.list(variables.organizationId),
      })
      // Invalidate role members to refresh user lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.roles.all,
      })
    },
  })
}

/**
 * Hook for updating a member's role in the organization
 */
export function useUpdateMemberRole() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      membershipId,
      data,
    }: {
      organizationId: string
      membershipId: string
      data: UpdateMemberRoleRequest
    }): Promise<OrganizationMembership> => {
      return await organizationsApi.updateMemberRole(organizationId, membershipId, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.memberships.list(variables.organizationId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.memberships.detail(variables.organizationId, variables.membershipId),
      })
      // Invalidate role members to refresh user lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.roles.all,
      })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for removing a user from an organization
 */
export function useRemoveMember() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      membershipId,
    }: {
      organizationId: string
      membershipId: string
    }): Promise<void> => {
      await organizationsApi.removeMember(organizationId, membershipId)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.memberships.list(variables.organizationId),
      })
      // Invalidate role members to refresh user lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.roles.all,
      })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for creating a new user and adding them to the organization in a single atomic operation
 */
export function useCreateUserAndAddToOrg() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      data,
    }: {
      organizationId: string
      data: CreateUserAndAddToOrgRequest
    }): Promise<OrganizationMembership> => {
      return await organizationsApi.createUserAndAddToOrg(organizationId, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.memberships.list(variables.organizationId),
      })
      // Invalidate role members to refresh user lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.roles.all,
      })
    },
    onError: () => {

    },
  })
}

// ============================================
// ROLE QUERIES
// ============================================

/**
 * Hook for fetching all roles for an organization
 * @param includeInactive - If true, includes inactive roles
 */
export function useOrganizationRoles(organizationId: string | null, includeInactive?: boolean) {
  return useQuery({
    queryKey: queryKeys.roles.list(organizationId || '', includeInactive),
    queryFn: async (): Promise<OrganizationRole[]> => {
      const params = includeInactive ? { include_inactive: true } : {}
      return await organizationsApi.getRoles(organizationId!, params)
    },
    enabled: !!organizationId,
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    retry: (failureCount, error: any) => {
      // Don't retry on 404
      if (error?.response?.status === 404) return false
      return failureCount < 1
    },
  })
}

/**
 * Hook for fetching details of a specific role
 */
export function useOrganizationRole(organizationId: string | null, roleId: string | null) {
  return useQuery({
    queryKey: queryKeys.roles.detail(organizationId || '', roleId || ''),
    queryFn: async (): Promise<OrganizationRole> => {
      return await organizationsApi.getRole(organizationId!, roleId!)
    },
    enabled: !!organizationId && !!roleId,
  })
}

/**
 * Hook for fetching all members who have a specific role in an organization
 */
export function useRoleMembers(organizationId: string | null, roleId: string | null) {
  return useQuery({
    queryKey: queryKeys.roles.members(organizationId || '', roleId || ''),
    queryFn: async (): Promise<OrganizationMembership[]> => {
      return await organizationsApi.getRoleMembers(organizationId!, roleId!)
    },
    enabled: !!organizationId && !!roleId,
  })
}

// ============================================
// ROLE MUTATIONS
// ============================================

/**
 * Hook for creating a new role in an organization
 */
export function useCreateRole() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      data,
    }: {
      organizationId: string
      data: CreateRoleRequest
    }): Promise<OrganizationRole> => {
      return await organizationsApi.createRole(organizationId, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.list(variables.organizationId) })
      // Invalidate role members to refresh user lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.roles.all,
      })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for updating role details
 */
export function useUpdateRole() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      roleId,
      data,
    }: {
      organizationId: string
      roleId: string
      data: UpdateRoleRequest
    }): Promise<OrganizationRole> => {
      return await organizationsApi.updateRole(organizationId, roleId, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.list(variables.organizationId) })
      queryClient.invalidateQueries({
        queryKey: queryKeys.roles.detail(variables.organizationId, variables.roleId),
      })
      // Invalidate role members to refresh user lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.roles.all,
      })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for deleting a role from an organization
 */
export function useDeleteRole() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      roleId,
    }: {
      organizationId: string
      roleId: string
    }): Promise<void> => {
      await organizationsApi.deleteRole(organizationId, roleId)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.list(variables.organizationId) })
      // Invalidate role members to refresh user lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.roles.all,
      })
    },
    onError: () => {

    },
  })
}

// ============================================
// PERMISSION QUERIES
// ============================================

/**
 * Hook for fetching role permissions with grant status
 * Returns all available permissions with is_granted flags for the role
 */
export function useRolePermissions(organizationId: string | null, roleId: string | null) {
  return useQuery({
    queryKey: queryKeys.permissions.rolePermissions(organizationId || '', roleId || ''),
    queryFn: async (): Promise<RolePermissionsResponse> => {
      return await organizationsApi.getRolePermissions(organizationId!, roleId!)
    },
    enabled: !!organizationId && !!roleId,
    staleTime: QUERY_CONFIG.LISTS.staleTime,
  })
}

/**
 * Hook for fetching object permissions for a role
 * Get all objects with access status for object permission management UI
 */
export function useObjectPermissions(
  organizationId: string | null,
  roleId: string | null,
  resourceType: string | null
) {
  return useQuery({
    queryKey: queryKeys.permissions.objectPermissions(
      organizationId || '',
      roleId || '',
      resourceType || ''
    ),
    queryFn: async (): Promise<ObjectPermissionsResponse> => {
      return await organizationsApi.getObjectPermissions(organizationId!, roleId!, resourceType!)
    },
    enabled: !!organizationId && !!roleId && !!resourceType,
    staleTime: 3 * 60 * 1000, // 3 minutes
  })
}

/**
 * Hook for fetching available object resource types for an organization
 */
export function useObjectResourceTypes(organizationId: string | null) {
  return useQuery({
    queryKey: queryKeys.permissions.objectResourceTypes(organizationId || ''),
    queryFn: async (): Promise<
      Record<string, Array<{ resource_type: string; name: string }>>
    > => {
      return await organizationsApi.getObjectResourceTypes(organizationId!)
    },
    enabled: !!organizationId,
    staleTime: QUERY_CONFIG.LISTS.staleTime,
  })
}

// ============================================
// PERMISSION MUTATIONS
// ============================================

/**
 * Hook for granting API permissions to a role
 */
export function useGrantPermissions() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      roleId,
      data,
    }: {
      organizationId: string
      roleId: string
      data: GrantPermissionsRequest
    }): Promise<{ message: string }> => {
      return await organizationsApi.grantPermissions(organizationId, roleId, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.permissions.rolePermissions(variables.organizationId, variables.roleId),
      })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for revoking API permissions from a role
 */
export function useRevokePermissions() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      roleId,
      data,
    }: {
      organizationId: string
      roleId: string
      data: RevokePermissionsRequest
    }): Promise<{ message: string }> => {
      return await organizationsApi.revokePermissions(organizationId, roleId, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.permissions.rolePermissions(variables.organizationId, variables.roleId),
      })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for granting object permission
 */
export function useGrantObjectPermission() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      resourceType,
      objectId,
      data,
    }: {
      organizationId: string
      resourceType: string
      objectId: string
      data: GrantObjectPermissionRequest
    }): Promise<{ message: string }> => {
      return await organizationsApi.grantObjectPermission(
        organizationId,
        resourceType,
        objectId,
        data
      )
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.permissions.objectPermissions(
          variables.organizationId,
          variables.data.role_id,
          variables.resourceType
        ),
      })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for revoking object permission
 */
export function useRevokeObjectPermission() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      resourceType,
      objectId,
      data,
    }: {
      organizationId: string
      resourceType: string
      objectId: string
      data: RevokeObjectPermissionRequest
    }): Promise<{ message: string }> => {
      return await organizationsApi.revokeObjectPermission(
        organizationId,
        resourceType,
        objectId,
        data
      )
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.permissions.objectPermissions(
          variables.organizationId,
          variables.data.role_id,
          variables.resourceType
        ),
      })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for fetching object permissions list for a resource type
 */
export function useObjectPermissionsList(
  organizationId: string | null,
  resourceType: string | null,
  roleId: string | null,
  params?: Record<string, any>
) {
  return useQuery({
    queryKey: queryKeys.permissions.objectPermissions(
      organizationId || '',
      resourceType || '',
      roleId || "",
      params ? JSON.stringify(params) : undefined
    ),
    queryFn: async (): Promise<any> => {
      return await organizationsApi.getObjectPermissionsList(organizationId!, resourceType!, roleId!, params)
    },
    enabled: !!organizationId && !!resourceType,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

/**
 * Hook for creating/updating object permission
 */
export function useCreateObjectPermission() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      resourceType,
      objectId,
      data,
    }: {
      organizationId: string
      resourceType: string
      objectId: string
      data: any
    }): Promise<any> => {
      return await organizationsApi.createObjectPermission(
        organizationId,
        resourceType,
        objectId,
        data
      )
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.permissions.objectPermissions(variables.organizationId, variables.resourceType),
      })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for deleting object permission
 */
export function useDeleteObjectPermission() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      resourceType,
      objectId,
      data,
    }: {
      organizationId: string
      resourceType: string
      objectId: string
      data: any
    }): Promise<void> => {
      await organizationsApi.deleteObjectPermission(organizationId, resourceType, objectId, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.permissions.objectPermissions(variables.organizationId, variables.resourceType),
      })
    },
    onError: () => {

    },
  })
}

// ============================================
// RESOURCE LIMITS QUERIES
// ============================================

/**
 * Hook for fetching organization usage details for a specific resource type
 */
export function useOrganizationUsage(
  organizationId: string | null,
  resourceType: ResourceType | null
) {
  return useQuery({
    queryKey: queryKeys.resourceLimits.organizationUsage(
      organizationId || '',
      resourceType || ''
    ),
    queryFn: async (): Promise<OrganizationUsageResponse> => {
      return await organizationsApi.getOrganizationUsage(organizationId!, resourceType!)
    },
    enabled: !!organizationId && !!resourceType,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false
      return failureCount < 1
    },
  })
}

/**
 * Hook for fetching all configured resource limits for a user
 * @param userId - Optional user ID (defaults to current user if not provided)
 */
export function useUserLimits(organizationId: string | null, userId?: string) {
  return useQuery({
    queryKey: queryKeys.resourceLimits.userLimits(organizationId || '', userId),
    queryFn: async (): Promise<UserLimitsListResponse> => {
      return await organizationsApi.getUserLimits(organizationId!, userId)
    },
    enabled: !!organizationId,
    staleTime: QUERY_CONFIG.LISTS.staleTime,
  })
}

/**
 * Hook for fetching user usage details for a specific resource type
 * @param userId - Optional user ID (defaults to current user if not provided)
 */
export function useUserUsage(
  organizationId: string | null,
  resourceType: ResourceType | null,
  userId?: string
) {
  return useQuery({
    queryKey: queryKeys.resourceLimits.userUsage(
      organizationId || '',
      resourceType || '',
      userId
    ),
    queryFn: async (): Promise<UserUsageResponse> => {
      return await organizationsApi.getUserUsage(organizationId!, resourceType!, userId)
    },
    enabled: !!organizationId && !!resourceType,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false
      return failureCount < 1
    },
  })
}

// ============================================
// RESOURCE LIMITS MUTATIONS
// ============================================

/**
 * Hook for setting or updating resource limit for an organization
 */
export function useSetOrganizationLimit() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      data,
    }: {
      organizationId: string
      data: SetOrganizationLimitRequest
    }): Promise<SetOrganizationLimitResponse> => {
      return await organizationsApi.setOrganizationLimit(organizationId, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.resourceLimits.organizationUsage(
          variables.organizationId,
          variables.data.resource_type
        ),
      })
    },
    onError: () => {

    },
  })
}

/**
 * Hook for setting or updating resource limit for a specific user
 */
export function useSetUserLimit() {
  return useMutation({
    mutationFn: async ({
      organizationId,
      data,
    }: {
      organizationId: string
      data: SetUserLimitRequest
    }): Promise<SetUserLimitResponse> => {
      return await organizationsApi.setUserLimit(organizationId, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.resourceLimits.userLimits(
          variables.organizationId,
          variables.data.user_id
        ),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.resourceLimits.userUsage(
          variables.organizationId,
          variables.data.resource_type,
          variables.data.user_id
        ),
      })
    },
    onError: () => {

    },
  })
}
